/**
 * The automation (feed) settings screen's data contract.
 *
 * `sections` is the whole of it — the tab set, the fields on each tab, their
 * current values and their state-driven branches — built from the input record
 * `InputEdit.jsx` already holds. Every field carries its legacy `data-field`
 * dot path in `name`, which is what `settings-changes.ts` emits its writes
 * against.
 *
 * THREE SURFACES SHARE IT: the basic screen (`InputEdit.jsx`), complex's
 * source page (`RouteSettings`' sibling) and the create-time dialog
 * (`InputModal.jsx`, which passes `create`).
 *
 * WHERE THE SHAPE COMES FROM. The tab set and the `fieldMappings` error lists
 * are `InputEditorButtonsMixin`'s; the fields, their option sets and every
 * branch below are the seven `Input*Panel.jsx` panels'. The labels are the
 * redesign's — sentence case, "Input" rather than "Feed" — and are rulings, not
 * drafts (§8). Read the option VALUES off `constants` rather than hard-coding
 * them, the way the panels do: they are `code/classes/types.php`'s numbers and
 * arrive with the bootstrap.
 *
 * ONE THING §6 ASKS FOR THAT THE RECORD DOES NOT HOLD: YouTube's channel /
 * playlist scope, which the host carries as `playlistScope` — see its field.
 * The location picker and the Smart Hashtags count were the other two and are
 * now built.
 */

import {
  type SettingsField,
  type SettingsSection,
  type TextRule,
  type ScheduleWindow,
  type EchoRow,
} from "@/shadcn/components/settings/settings-form"
import {
  type FeedSubtype,
  type SourceType,
} from "@/shadcn/components/settings/settings-vocab"

import {
  type Constants,
  type LegacyRecord,
  FILTER_PATHS,
  activeLast,
  affixHashtagFault,
  attachErrors,
  choice,
  filterFields,
  flag,
  options,
  readPath,
  text,
  validationMessage,
} from "@/lib/settings-sections"
import { type InputPlaylist } from "@/lib/input-playlists"

export type FeedSectionsContext = {
  constants: Constants
  /** `user.perms.resources` — the plan gates on frequency and hashtags. */
  resources: LegacyRecord
  /** A free plan sees the faster frequencies and PuSH as locked. */
  isFreeUser: boolean
  /** Agency / Plus / Agency Plus get X's 5-minute floor instead of 15. */
  fastPullUser: boolean
  /**
   * `app.flags.routeViewType`, which gates update windows and photo posting.
   *
   * Either copy would do — `user.detail.routeViewType` holds the same number
   * off the same `USER_FETCH` payload, and both are genuinely numbers. This
   * one is named because it is what `InputUpdatePanel` reads for the update
   * windows rule, which is the stricter of the two.
   */
  routeViewType: number
  /**
   * The related-hashtag search: what came back, and whether it is still
   * running. `state.utilities.relatedTags` plus the loading flag.
   */
  related?: { tags: string[]; loading: boolean }
  /**
   * YouTube, while creating: the channel's playlists, from
   * `fetchInputPlaylists`. `list` is null when there was nothing to fetch
   * with or the request failed, and an empty array when none came back.
   */
  playlists?: { list: InputPlaylist[] | null; loading: boolean }
  /**
   * YouTube, while creating: the reader's pick on "Post these videos", once
   * they have made one.
   *
   * HOST-HELD BECAUSE THE RECORD CANNOT SAY IT. `attrs.playlistID` is the only
   * thing the backend reads, and no id means the whole channel — so "One
   * playlist, none chosen yet" has no stored form, and deriving the control
   * from the record alone snaps it straight back to "Whole channel". Legacy
   * holds the same choice in `YouTubeInputConfigPanel`'s own state.
   */
  playlistScope?: "channel" | "playlist"
  /**
   * Field id -> a message the last edit was refused with, e.g. a duplicate
   * hashtag or one tag past the cap.
   *
   * LOCAL, NOT THE API'S. These come from `feedFieldError`, which runs before
   * the write and stops it; the host holds the message until the next edit to
   * that field clears it. Kept out of the section functions and attached in
   * one pass at the end, so a new validated field needs no plumbing.
   */
  fieldErrors?: Record<string, string>
  /** `validationTree(app)` — the server's complaint about the last write. */
  validation?: LegacyRecord | null
  /**
   * The source is being CREATED rather than edited, which changes the first
   * tab and nothing else.
   *
   * Legacy swaps it outright — `InputEditorButtonsMixin` returns `setup` in
   * place of `detail` for the create path — because three of that tab's rows
   * only make sense once a source exists (its URL, whether it is active, and
   * reconnecting it), while one only makes sense before it does: what to do
   * with the backlog on the first pull.
   */
  create?: boolean
}

/* ─────────────────────────────────────────────────────────── vocabulary ── */

/** `INPUT_TYPE_*` -> the key the screens use. */
export function feedSourceType(
  input: LegacyRecord | null | undefined,
  constants: Constants
): SourceType {
  switch (parseInt(String(input?.type ?? ""), 10)) {
    case constants.INPUT_TYPE_TWITTER:
      return "twitter"
    case constants.INPUT_TYPE_INSTAGRAM:
      /* The retired Instagram type (C9). Legacy still branches on it, so
         records exist; without this they would fall through to RSS and be
         offered the whole feed-only Advanced tab. */
    case constants.INPUT_TYPE_INSTAGRAM_BUSINESS:
      return "instagram"
    case constants.INPUT_TYPE_FACEBOOK_PAGE:
      return "facebook"
    case constants.INPUT_TYPE_TIKTOK:
      return "tiktok"
    case constants.INPUT_TYPE_YOUTUBE:
      return "youtube"
    default:
      /* Pinterest and the other guided flavours are INPUT_TYPE_FEED with a
         subtype, so they land here and behave as RSS — which is the rule
         settings-vocab.ts states. */
      return "rss"
  }
}

/**
 * `INPUT_SUBTYPE_*` -> the label key, for the header's meta line only.
 *
 * Read off both spellings because the input record's key for this is not
 * settled: the create flow writes `subtype`, `Input.php` holds `subType`.
 * Label-only, so an unrecognised value simply falls back to "RSS feed".
 */
export function feedSubtype(
  input: LegacyRecord | null | undefined,
  constants: Constants
): FeedSubtype | undefined {
  const stored = parseInt(String(input?.subtype ?? input?.subType ?? ""), 10)

  if (!stored) return undefined

  const byCode: [number, FeedSubtype][] = [
    [constants.INPUT_SUBTYPE_PINTEREST, "pinterest"],
    [constants.INPUT_SUBTYPE_EBAY, "ebay"],
    [constants.INPUT_SUBTYPE_ETSY, "etsy"],
    [constants.INPUT_SUBTYPE_MEDIUM, "medium"],
    [constants.INPUT_SUBTYPE_SHOPIFY, "shopify"],
    [constants.INPUT_SUBTYPE_SQUARESPACE, "squarespace"],
    [constants.INPUT_SUBTYPE_VIMEO, "vimeo"],
    [constants.INPUT_SUBTYPE_YOUTUBE, "youtube"],
    [constants.INPUT_SUBTYPE_SUBSTACK, "substack"],
  ]

  return byCode.find(([code]) => code === stored)?.[1]
}

/** The redesign's tab labels. */
const SECTION_LABEL: Record<string, string> = {
  detail: "General",
  updates: "Updates",
  hashtags: "Hashtags",
  text: "Item text",
  filters: "Filters",
  location: "Location",
  advanced: "Advanced",
}

/**
 * Section id -> the validation keys that belong to it, for the tab's error
 * badge. Legacy's `fieldMappings` verbatim, plus the container keys its own
 * search could not reach (`attrs`, `sched`, `rules`, `socialOverrides`).
 */
export const FEED_ERROR_KEYS: Record<string, string[]> = {
  detail: [
    "activeFlag",
    "firstPullFlag",
    "name",
    "repost",
    "echoPrefix",
    "postFlag",
    "typeFilter",
    "socialOverrides",
    "postStyle",
    "postText",
    "playlistID",
  ],
  updates: [
    "pullFreq",
    "numItems",
    "postLimit",
    "itemTrickle",
    "pushEnabled",
    "sched",
  ],
  hashtags: [
    "hashStatic",
    "hashPool",
    "poolNum",
    "poolActive",
    "staticActive",
    "autoHash",
    "autoHashNum",
    "autoHashSpaces",
    "autoHashSource",
  ],
  text: ["rules", "prefixText", "postfixText", "prefixSpace", "postfixSpace"],
  filters: [
    "filtersTitle",
    "filtersCategory",
    "filtersContent",
    "filtersAuthor",
    "filtersLink",
    "filterBehavior",
    "andFilter",
    "orFilter",
    "notFilter",
  ],
  location: ["defaultGeo", "useGeo", "overrideItemGeo"],
  advanced: [
    "postFlag",
    "useDateFlag",
    "skipNoTitleFlag",
    "preferSummary",
    "ignoreSortDate",
    "ignoreOrigLink",
    "postImages",
    "exclude_replies",
    "include_rts",
  ],
}

/**
 * The fields whose single `name` stands for more than one stored value, so the
 * dirty check knows what to compare. The filter checkbox group is five
 * booleans legacy never gave a collective field.
 */
export const FEED_COMPOSITE_PATHS: Record<string, string[]> = {
  "input.filters": FILTER_PATHS,
}

/* ───────────────────────────────────────────────────── record -> widget ── */

/**
 * `attrs.repost` is a list of MINUTE offsets and `attrs.echoPrefix` a parallel
 * list of prefixes; the widget wants days and hours. `EchoComponent` does the
 * same arithmetic in the other direction.
 */
function echoRows(input: LegacyRecord): EchoRow[] {
  const repost: unknown[] = Array.isArray(input?.attrs?.repost)
    ? input.attrs.repost
    : []
  const prefixes: unknown[] = Array.isArray(input?.attrs?.echoPrefix)
    ? input.attrs.echoPrefix
    : []

  return repost.map((entry, i) => {
    const minutes = parseInt(String(entry ?? ""), 10) || 0

    return {
      id: echoRowId(i),
      days: String(Math.floor(minutes / 1440)),
      hours: String(Math.floor((minutes % 1440) / 60)),
      prefix: text(prefixes[i]),
    }
  })
}

/**
 * A `Schedules` row -> an update window. The field names differ, the shapes do
 * not; `ScheduleWindow`'s own doc comment carries the mapping, including why
 * `end: "00:00:00"` means "posts once at start".
 */
function scheduleWindows(input: LegacyRecord): ScheduleWindow[] {
  const stored: LegacyRecord[] = Array.isArray(input?.sched) ? input.sched : []

  return stored.map((window, i) => ({
    id: text(window.s_id) || `sch-${i}`,
    days: parseInt(String(window.s_days ?? ""), 10) || 0,
    start: text(window.s_startTime) || "00:00:00",
    end: text(window.s_endTime) || "00:00:00",
  }))
}

/**
 * A find-and-replace rule.
 *
 * `deleted` rows are skipped: legacy hides a removed rule until Save rather
 * than splicing it, because the rule list is mutable where React assumes it is
 * not (`InputEdit.save` does the actual removal).
 *
 * `action` is derived when absent, the way `InputTextRule` derives it — an
 * empty replacement means the rule removes rather than replaces.
 */
function textRules(
  input: LegacyRecord,
  validation: LegacyRecord | null | undefined
): TextRule[] {
  /*
   * `$errs['rules'][$idx]` is indexed against the array that was SENT, and
   * `InputEdit.save` strips `deleted` rules before sending — then assigns the
   * filtered list back onto state. So by the time any error exists the stored
   * array is the sent array and the two indexes agree. They would not agree
   * before a save, and there is no error to place then.
   */
  const ruleErrors = Array.isArray(validation?.rules) ? validation.rules : []
  const stored: LegacyRecord[] = Array.isArray(input?.rules) ? input.rules : []

  /*
   * MAP BEFORE FILTER, so `i` is the index in the STORED array rather than in
   * the visible one. A legacy rule has no id of its own, so its position is
   * its identity, and a removal marks `deleted` instead of splicing — which
   * means the two indexes diverge the moment a rule is removed. Keying the
   * visible rows off the filtered index would silently retarget every edit
   * below a removed rule.
   */
  return stored
    .map((rule, i) => {
      const fields: string = text(rule.fields)
      const action =
        rule.action === "replace" || rule.action === "remove"
          ? rule.action
          : text(rule.replace) !== ""
            ? "replace"
            : "remove"

      /* A row can fail on more than one count — an empty search AND no field
         ticked — and each is separately fixable, so both are said. */
      const failures = ruleErrors[i]
      const message =
        failures && typeof failures === "object"
          ? Object.values(failures).filter((entry) => typeof entry === "string").join(" ")
          : undefined

      return {
        deleted: rule?.deleted !== undefined,
        id: ruleRowId(i),
        error: message || undefined,
        action,
        search: text(rule.search),
        replace: text(rule.replace),
        // Legacy's own spellings: `case` (a reserved word, hence the quotes)
        // and `find: 'whole'`.
        caseSensitive: flag(rule["case"]),
        wholeWord: rule.find === "whole",
        searchTitle: fields.indexOf("title") !== -1,
        searchBody: fields.indexOf("body") !== -1,
      }
    })
    .filter((rule) => !rule.deleted)
    .map(({ deleted: _deleted, ...rule }) => rule)
}

/* ────────────────────────────────────────────────────── row identities ── */

/**
 * A rule row's id is its position in the stored array, because a legacy rule
 * has nothing else to be identified by. Encoded and decoded here together so
 * the two cannot drift; `settings-changes.ts` reads them back.
 */
export function ruleRowId(index: number): string {
  return `rule-${index}`
}

export function ruleIndex(id: string): number {
  return parseInt(id.replace("rule-", ""), 10)
}

/** Same for an echo row, which is a position in `attrs.repost`. */
export function echoRowId(index: number): string {
  return `echo-${index}`
}

export function echoIndex(id: string): number {
  return parseInt(id.replace("echo-", ""), 10)
}

/* ──────────────────────────────────────────────────────────── the tabs ── */

function detailSection(
  input: LegacyRecord,
  clean: LegacyRecord | null | undefined,
  source: SourceType,
  ctx: FeedSectionsContext
): SettingsSection {
  const { constants } = ctx
  const fields: SettingsField[] = []
  const isSocialSource = source !== "rss"
  const complex = ctx.routeViewType === constants.ROUTE_VIEW_ADVANCED
  const creating = Boolean(ctx.create)

  /*
   * ONE SLOT, TWO STATES — which is what the id is named for. An OAuth source
   * with no stored credential offers Reconnect; one that has just come back
   * through OAuth says so instead, because the credential is not saved until
   * the record is. `InputDetailPanel` swaps the same fieldset the same way.
   *
   * It leads the tab, where legacy has it mid-panel: a source that needs
   * reconnecting posts nothing until it is, so it outranks every setting
   * below it. It never meets the URL row — that one is RSS-only.
   */
  if (isSocialSource && !creating) {
    fields.push(
      input.credID
        ? {
            id: "source-slot",
            label: "Reconnect your input",
            kind: "static",
            staticValue:
              "We have fresh credentials for this input. Save to finish the job.",
            span: "full",
          }
        : {
            id: "source-slot",
            label: "Reconnect your input",
            kind: "action",
            actionLabel: "Reconnect",
            /* `sysActionFlag` is legacy's "this is broken" marker, and it is
               what swaps the button from secondary to primary there. */
            urgent: flag(input.sysActionFlag),
          }
    )
  }

  /* Nothing to show the URL of, nothing to reconnect, and nothing to
     deactivate — all three describe a source that already exists. */
  if (!isSocialSource && !creating) {
    fields.push({
      id: "url",
      hint: "We cannot change the URL of an existing source. Add a new source instead, so your stats stay consistent and old items do not re-post.",
      name: "input.url",
      /*
       * THE ONE LABEL THE TWO SCREENS DISAGREE ON. Complex's copy is frozen
       * (§8), so it keeps "Source URL" where basic says "Input URL".
       *
       * Whether the rest of this screen should follow legacy's complex UI and
       * say "feed" throughout — "Feed Active", "Delete Feed" — is open, and
       * `source-settings.tsx` flags it rather than taking it. Only the label
       * §8 actually names is swapped here.
       */
      label: complex ? "Source URL" : "Input URL",
      kind: "static",
      staticValue: text(input.url),
      span: "full",
    })
  }

  fields.push({
    id: "name",
    name: "input.name",
    label: "Name",
    kind: "input",
    value: text(input.name),
    required: true,
  })

  fields.push({
    id: "post-flag",
    name: "input.postFlag",
    label: "When should we post new items?",
    kind: "segmented",
    options: options([
      { value: constants.POST_NOW, label: "Post immediately" },
      { value: constants.POST_QUEUE, label: "Add to queue and post later" },
    ]),
    value: choice(input.postFlag) ?? String(constants.POST_NOW),
  })

  /*
   * NEXT POST — complex only, and only while the source has just been switched
   * on. It asks what to do with the backlog on the first pull, so for a source
   * that is already running the question is already answered and the control
   * does nothing.
   *
   * IT IS A CREATION-TIME FIELD IN PRACTICE, which is the context the code
   * alone does not give. `InputDetailPanel` renders under two parents —
   * `InputNew` while a feed is being configured, and `InputEdit` afterwards —
   * and its `!this.props.initialActiveState` guard is inert, because nothing
   * ever passes that prop: it is declared in two propTypes blocks and supplied
   * by no caller, so the guard is `!undefined`. What keeps the field to new
   * feeds is how people reach it, not that condition.
   *
   * So this gate is not a narrowing of anything anyone uses. The create path
   * now runs on these screens too (`InputModal.jsx`), and asks the question
   * outright through `create` above; here, on the edit screen, the condition
   * below keeps the field to the one moment it still has an answer — a source
   * being brought to life.
   *
   * The verb tracks the field above it, exactly as legacy composes it: the
   * choice reads "Post ..." or "Queue ..." depending on where new items go.
   */
  const justActivated = flag(input.activeFlag) && !flag(clean?.activeFlag)

  /*
   * AT CREATE IT IS ALWAYS ASKED, and in plainer words. `InputBehaviorComponent`
   * renders it unconditionally there (`showFirstPull` defaults true) and its
   * options are bare — "The most recent item" — because nothing has been
   * decided yet. On an existing source the same question is phrased against
   * where new items go, "Post ..." or "Queue ...", which is a choice the
   * reader has already made by then.
   */
  if (creating) {
    fields.push({
      id: "first-pull",
      name: "input.firstPullFlag",
      label: "What should we post first?",
      kind: "select",
      options: options([
        { value: constants.FIRSTPULL_SINGLE, label: "The most recent item" },
        { value: constants.FIRSTPULL_OFF, label: "The next new item published" },
        { value: constants.FIRSTPULL_ON, label: "The most recent 10 items now" },
      ]),
      value: choice(input.firstPullFlag),
    })
  } else if (complex && justActivated) {
    const verb =
      String(input.postFlag ?? constants.POST_NOW) === String(constants.POST_NOW)
        ? "Post"
        : "Queue"

    fields.push({
      id: "first-pull",
      name: "input.firstPullFlag",
      label: "Next post",
      /* Three options whose labels run to a sentence — past what the segmented
         control is for, per the SEGMENTED VS SELECT rule in `SettingsField`. */
      kind: "select",
      options: options([
        {
          value: constants.FIRSTPULL_SINGLE,
          label: `${verb} the most recent item now`,
        },
        {
          value: constants.FIRSTPULL_OFF,
          label: `${verb} when a new item is published`,
        },
        {
          value: constants.FIRSTPULL_ON,
          label: `${verb} all items (up to max)`,
        },
      ]),
      value: choice(input.firstPullFlag),
      help: "What we do with what is already there, the first time we check.",
    })
  }

  /* X has no echo: `InputDetailPanel` withholds the whole control for it. */
  if (source !== "twitter") {
    fields.push({
      id: "echo",
      hint: "Recycle your content for a second look. We post an item, then queue it to post again later.",
      name: "input.attrs.repost",
      label: "Echo",
      kind: "echo",
      checked: Array.isArray(input?.attrs?.repost) && input.attrs.repost.length > 0,
      echoRows: echoRows(input),
      locked: !flag(ctx.resources?.repost),
      span: "full",
    })
  }

  /* No Active switch here (Emily, 2026-10-01): an automation is paused and
     resumed from its list, not its settings. */

  /* The three OAuth sources that expose the feed / reels / stories bitmask.
     TikTok returns videos only, so its panel has the block commented out. */
  if (source === "instagram" || source === "facebook") {
    const typeFilter = parseInt(String(input?.attrs?.typeFilter ?? ""), 10) || 0
    const bits = [
      { value: constants.INPUT_SOURCE_FILTER_FEED, label: "Feed posts" },
      { value: constants.INPUT_SOURCE_FILTER_REELS, label: "Reels" },
      { value: constants.INPUT_SOURCE_FILTER_STORIES, label: "Stories" },
    ]

    fields.push({
      id: "type-filter",
      name: "input.attrs.typeFilter",
      label: "Post the following items",
      kind: "checkbox",
      options: options(bits),
      values: bits
        .filter((bit) => typeFilter & bit.value)
        .map((bit) => String(bit.value)),
    })
  }

  if (source === "instagram" || source === "facebook" || source === "tiktok") {
    fields.push({
      id: "post-style",
      hint: "Native media shows photos and videos in full; adding Link also links back to the original post. Shared link posts a link card instead.",
      name: "input.socialOverrides.postStyle",
      label: "Post photos and videos as",
      kind: "segmented",
      options: options([
        { value: constants.SOC_OVRD_STYLE_MEDIA, label: "Native media" },
        { value: constants.SOC_OVRD_STYLE_BOTH, label: "Native media + link" },
        { value: constants.SOC_OVRD_STYLE_LINK, label: "Shared link" },
      ]),
      value: choice(readPath(input, "socialOverrides.postStyle")),
    })
  }

  if (isSocialSource && source !== "twitter") {
    /* The three words for the same three choices. YouTube's are its own; the
       other three follow the network's noun for a post's text. */
    const labels: Record<string, [string, string, string]> = {
      instagram: ["Caption + hashtags", "Caption", "No text"],
      facebook: ["Message + hashtags", "Message", "No text"],
      tiktok: ["Description + hashtags", "Description", "No text"],
      youtube: ["Title + description", "Title", "No text"],
    }
    const [both, caption, none] = labels[source]

    fields.push({
      id: "post-text",
      name: "input.socialOverrides.postText",
      label: "Include text in posts",
      kind: "segmented",
      options: options([
        { value: constants.SOC_OVRD_TEXT_BOTH, label: both },
        { value: constants.SOC_OVRD_TEXT_CAPTION, label: caption },
        { value: constants.SOC_OVRD_NONE, label: none },
      ]),
      value:
        choice(readPath(input, "socialOverrides.postText")) ??
        String(constants.SOC_OVRD_TEXT_BOTH),
    })
  }

  /* Chosen once, at creation: a source's channel or playlist can't be changed
     afterwards, as in legacy. */
  if (source === "youtube" && creating) {
    /*
     * `attrs.playlistID` IS THE WHOLE STORED STATE — `InputYoutube` pulls that
     * playlist when it is set and the channel's uploads when it is empty. The
     * scope control writes it too (clearing it for the whole channel), which
     * is why both fields carry its name: the tab still reads as edited once
     * the playlist row has gone.
     */
    const picked = text(readPath(input, "attrs.playlistID"))
    const scope = ctx.playlistScope ?? (picked ? "playlist" : "channel")
    const loading = Boolean(ctx.playlists?.loading)
    const list = ctx.playlists?.list
    /* Nothing to pick from, as distinct from a channel with no playlists. */
    const unavailable = !loading && !Array.isArray(list)

    fields.push({
      id: "scope",
      help: loading
        ? "Loading playlists…"
        : unavailable
          ? "We couldn't load this channel's playlists."
          : "Choose the whole channel, or a single playlist.",
      name: "input.attrs.playlistID",
      label: "Post these videos",
      kind: "segmented",
      options: [
        { value: "channel", label: "Whole channel" },
        { value: "playlist", label: "One playlist" },
      ],
      value: scope,
      disabled: loading || unavailable,
    })

    if (scope === "playlist" && Array.isArray(list)) {
      /* A stored id the channel no longer has would leave the trigger blank;
         the placeholder asks for a new pick instead. */
      const known = list.some((playlist) => text(playlist.id) === picked)
      /*
       * An empty list is ambiguous: `Youtube::getPlaylists` returns [] when the
       * API call fails as well as for a channel with no playlists. So the empty
       * box and a saved playlist's warning say only what holds either way.
       */
      let warning: string | undefined

      if (picked && !known) {
        warning = list.length
          ? "The saved playlist is no longer on this channel. Choose another."
          : "We couldn't find this source's playlist on the channel. Choose Whole channel, or check the playlist on YouTube."
      } else if (!picked && list.length) {
        warning = "Choose a playlist. Without one, we post the whole channel."
      }

      fields.push({
        id: "playlist",
        name: "input.attrs.playlistID",
        label: "Playlist",
        kind: "select",
        dependsOn: "scope",
        options: list.map((playlist) => ({
          value: text(playlist.id),
          label: text(playlist.title),
        })),
        value: known ? picked : undefined,
        placeholder: list.length ? "Choose a playlist" : "No playlists found",
        disabled: list.length === 0,
        warning,
        /* The server files this at the top of the tree, not under `attrs`. */
        error: validationMessage(ctx.validation ?? null, "playlistID"),
      })
    }
  }

  /* Legacy names this tab `setup` while creating and `detail` afterwards. The
     id stays one thing so the host has one mapping; only the label moves. */
  return {
    id: "detail",
    label: creating ? "Setup" : SECTION_LABEL.detail,
    /* Active is pushed halfway up this function, before the per-source
       fields; this puts it back at the foot of the card. */
    fields: activeLast(fields),
  }
}

function updatesSection(
  input: LegacyRecord,
  source: SourceType,
  ctx: FeedSectionsContext
): SettingsSection {
  const { constants } = ctx
  const fields: SettingsField[] = []

  /*
   * The frequency list is the PLAN's, not a constant: `pullFrequencyList` comes
   * back on the user's permissions. X gets a floor on top of it — 5 minutes for
   * the paid tiers, 15 for everyone else — because its API rate limits make the
   * faster options unusable rather than merely expensive.
   */
  let frequencies: number[] = Array.isArray(ctx.resources?.pullFrequencyList)
    ? ctx.resources.pullFrequencyList.map((freq: unknown) =>
        parseInt(String(freq), 10)
      )
    : []

  if (source === "twitter") {
    const floor = ctx.fastPullUser ? 5 : 15

    frequencies = frequencies.filter((freq) => freq >= floor)
  }

  fields.push({
    id: "pull-freq",
    name: "input.pullFreq",
    label: "Update frequency",
    kind: "select",
    options: options(
      frequencies.map((freq) => ({ value: freq, label: frequencyLabel(freq) }))
    ),
    value: choice(input.pullFreq) ?? "30",
    width: "sm",
    /* Legacy renders an "Unlock faster updates" link rather than disabling the
       select; the redesign has one affordance for a plan gate, so it is this. */
    locked: ctx.isFreeUser,
  })

  fields.push({
    id: "num-items",
    name: "input.numItems",
    label: "Maximum posts per update",
    kind: "input",
    value: text(input.numItems),
    required: true,
    width: "xs",
  })

  fields.push({
    id: "post-limit",
    name: "input.postLimit",
    label: "Maximum posts per day",
    kind: "input",
    value: text(input.postLimit),
    required: true,
    width: "xs",
  })

  fields.push({
    id: "trickle",
    hint: "With trickle off, items we find beyond the per-update maximum are marked seen and never post. With it on, they queue up and post later.",
    name: "input.itemTrickle",
    label: "Trickle",
    kind: "select",
    options: options([
      { value: 0, label: "Trickle off" },
      { value: 1, label: "Post newest items first" },
      { value: 2, label: "Post oldest items first" },
      { value: 3, label: "Randomize item order" },
    ]),
    value: choice(input.itemTrickle) ?? "0",
    width: "sm",
  })

  /* PuSH is a feed-protocol subscription, so X — which is polled through its
     own API — never offered it. */
  if (source !== "twitter") {
    fields.push({
      id: "push-enabled",
      hint: "If your source supports PuSH we subscribe to its hub and receive items as you publish them. Turn this off if a PuSH source is posting unreliably.",
      name: "input.pushEnabled",
      label: "Subscribe to PuSH updates",
      kind: "toggle",
      checked: ctx.isFreeUser ? false : flag(input.pushEnabled),
      locked: ctx.isFreeUser,
    })
  }

  /*
   * COMPLEX ONLY. `InputUpdatePanel` wraps the whole Update Window fieldset in
   * `routeViewType == ROUTE_VIEW_ADVANCED`, and it stays that way: the basic
   * UI has never offered posting windows, and §6 listing the field says which
   * paths exist, not which audience sees them.
   */
  if (ctx.routeViewType === constants.ROUTE_VIEW_ADVANCED) {
    fields.push({
      id: "sched",
      hint: "Inside a window we check your source and post on the frequency above. Outside every window we do not post. With no windows at all we post around the clock.",
      name: "input.sched",
      label: "Update windows",
      kind: "schedule",
      windows: scheduleWindows(input),
      span: "full",
    })
  }

  return { id: "updates", label: SECTION_LABEL.updates, fields }
}

/** "Every 30 minutes", "Every hour", "Once a day" — legacy's own wording. */
function frequencyLabel(freq: number): string {
  if (freq < 60) return `Every ${freq} minutes`
  if (freq >= 1440) return "Once a day"
  if (freq === 60) return "Every hour"

  return `Every ${freq / 60} hours`
}

/* Legacy's own style keys. They are STRINGS, not numbers: the style is a
   derived control with no stored field of its own — `setHashtagStyle` reads it
   back off which of the four underlying flags is set, and `changeStyle` writes
   those flags rather than the style. Exported because `settings-changes.ts`
   has to perform that write. */
export const STYLE_OFF = "0"
export const STYLE_AUTO_DETECT = "STYLE_AUTO_DETECT"
export const STYLE_FIXED_DYNAMIC = "STYLE_FIXED_DYNAMIC"
export const STYLE_AI_GENERATED = "STYLE_AI_GENERATED"

/** The `data-field` path the style select reports under. */
export const HASHTAG_STYLE_FIELD = "input.attrs.hashTags.style"

/** `InputHashtagPanel.setHashtagStyle`, unchanged. */
function hashtagStyle(input: LegacyRecord): string {
  const attrs = input?.attrs ?? {}
  const hashTags = attrs.hashTags ?? {}

  if (flag(attrs.autoHash)) return STYLE_AUTO_DETECT
  if (flag(hashTags.smartHashtags)) return STYLE_AI_GENERATED

  // staticActive defaults to ON for records written before the flag existed.
  const staticActive =
    hashTags.staticActive === undefined ? true : flag(hashTags.staticActive)
  const staticTags = Array.isArray(hashTags.hashStatic) ? hashTags.hashStatic : []

  if ((staticActive && staticTags.length) || flag(hashTags.poolActive)) {
    return STYLE_FIXED_DYNAMIC
  }

  return STYLE_OFF
}

function hashtagsSection(
  input: LegacyRecord,
  ctx: FeedSectionsContext
): SettingsSection {
  const { constants } = ctx
  const hashTags = input?.attrs?.hashTags ?? {}
  const style = hashtagStyle(input)
  const fields: SettingsField[] = [
    {
      id: "hashtag-style",
      help: "Only one style posts at a time.",
      name: HASHTAG_STYLE_FIELD,
      label: "Hashtag style",
      kind: "select",
      options: [
        { value: STYLE_OFF, label: "Hashtags off" },
        { value: STYLE_AUTO_DETECT, label: "Auto-detected hashtags" },
        { value: STYLE_FIXED_DYNAMIC, label: "Fixed and dynamic hashtags" },
        { value: STYLE_AI_GENERATED, label: "Smart hashtags" },
      ],
      value: style,
    },
  ]

  if (style === STYLE_AUTO_DETECT) {
    fields.push(
      {
        id: "auto-hash",
        hint: "We scan your items and their linked pages for category and topic tags, and include them as hashtags.",
        name: "input.attrs.autoHash",
        label: "Placement",
        kind: "segmented",
        options: options([
          { value: 1, label: "Start of post" },
          { value: 2, label: "End of post" },
          { value: 3, label: "Inline" },
        ]),
        value: choice(input?.attrs?.autoHash),
        locked: !flag(ctx.resources?.autoHash),
      },
      {
        id: "auto-hash-num",
        name: "input.attrs.autoHashNum",
        label: "Maximum hashtags per post",
        kind: "segmented",
        /* 1-5, not 0-5: the auto-detect count drops the zero, because turning
           it off is what the style select above is for. */
        options: options([1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }))),
        value: choice(input?.attrs?.autoHashNum),
      },
      {
        id: "auto-hash-spaces",
        name: "input.attrs.autoHashSpaces",
        label: "Blank space handling",
        kind: "segmented",
        options: options([
          { value: 0, label: "Remove spaces" },
          { value: 1, label: "Change spaces to underscores" },
        ]),
        value: choice(input?.attrs?.autoHashSpaces) ?? "0",
      },
      {
        id: "auto-hash-source",
        name: "input.attrs.autoHashSource",
        label: "Where should we look for hashtags?",
        kind: "segmented",
        options: options([
          { value: constants.HASH_SRC_PAGE, label: "The linked web page" },
          { value: constants.HASH_SRC_ITEM, label: "The feed item" },
        ]),
        value: choice(input?.attrs?.autoHashSource),
      }
    )
  }

  if (style === STYLE_FIXED_DYNAMIC) {
    const staticLocked = !flag(ctx.resources?.hashStatic)

    fields.push(
      {
        id: "static-active",
        name: "input.attrs.hashTags.staticActive",
        label: "Use fixed hashtags",
        kind: "toggle",
        checked:
          hashTags.staticActive === undefined
            ? true
            : flag(hashTags.staticActive),
        locked: staticLocked,
      },
      {
        id: "hash-static",
        placeholder: "e.g. #news",
        hint: "These appear on every item we post for you.",
        name: "input.attrs.hashTags.hashStatic",
        label: "Fixed hashtags",
        kind: "tags",
        values: Array.isArray(hashTags.hashStatic) ? hashTags.hashStatic : [],
        emptyLabel: "No fixed hashtags yet.",
        locked: staticLocked,
        span: "full",
        help: `Up to ${constants.HASH_STATIC_MAX} tags, added to every post.`,
        /* Legacy's own sentence for the one state where a gated field still
           has data in it. Worth keeping: the tags are retained, and without
           this the user reads the lock as "we deleted them". */
        warning:
          staticLocked && Array.isArray(hashTags.hashStatic) && hashTags.hashStatic.length
            ? "We'll hold on to your fixed hashtags, but they won't post or be editable on your current plan."
            : undefined,
      },
      {
        id: "hash-pool",
        placeholder: "e.g. #contentmarketing",
        hint: "We attach a different selection from your pool to each post.",
        name: "input.attrs.hashTags.hashPool",
        label: "Dynamic hashtag pool",
        kind: "tags",
        values: Array.isArray(hashTags.hashPool) ? hashTags.hashPool : [],
        emptyLabel: "Nothing in the pool yet.",
        /*
         * "Find related" searches on whatever is in the add box and offers
         * what comes back underneath it. Legacy's equivalent looks like a
         * dialog and is not one — `hashtag-modal` is a div inside the fieldset
         * — so showing the results inline is the same shape, not a departure.
         */
        secondaryAction: "Find related",
        suggestions: ctx.related?.tags ?? [],
        suggestionsLoading: ctx.related?.loading,
        locked: !flag(ctx.resources?.hashPool),
        span: "full",
        help: `Up to ${constants.HASH_POOL_MAX} tags. We pick from the pool for each post.`,
        /*
         * INSIDE THE POOL, not beside it. It counts the list above it and
         * means nothing without it, so a row of its own — with the divided
         * list's hairline between them — read as an unrelated setting.
         */
        listField: {
          id: "pool-num",
          name: "input.attrs.hashTags.poolNum",
          label: "Hashtags from your pool per post",
          kind: "segmented",
          options: options([0, 1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }))),
          value: choice(hashTags.poolNum) ?? "0",
          locked: !flag(ctx.resources?.hashPool),
        },
      }
    )
  }

  /*
   * §6 lists neither this field nor the style value that reveals it, so the
   * label is ours. Legacy offers 1-5 and seeds 5 when the style is picked
   * (`changeStyle`), which is what the default below matches.
   */
  if (style === STYLE_AI_GENERATED) {
    fields.push({
      id: "smart-qty",
      name: "input.attrs.hashTags.smartHashtagQty",
      label: "Maximum generated hashtags per post",
      kind: "segmented",
      options: options([1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }))),
      value: choice(hashTags.smartHashtagQty) ?? "5",
      locked: !flag(ctx.resources?.smartHashtags),
      /* The kit splits this in two - a "Smart hashtags" toggle explaining what
         the feature does, and this count carrying legacy's AI disclaimer. Prod
         has no such toggle, so the explanation becomes this field's tooltip and
         the disclaimer stays the visible line, which is where the kit puts it. */
      hint: "We generate hashtags from the content of each post.",
      help: "Generated results may not always be accurate or perfectly tailored.",
    })
  }

  return {
    id: "hashtags",
    label: SECTION_LABEL.hashtags,
    /*
     * EVERY FIELD AFTER THE FIRST IS GOVERNED BY IT — each of the three
     * branches above is pushed because `style` chose it, so marking them by
     * position covers all three and whatever a fourth style adds, rather than
     * a list of ids that would go stale the first time one is renamed.
     *
     * Presentational only: which branch runs is still decided above.
     */
    fields: fields.map((field, i) =>
      i === 0 ? field : { ...field, dependsOn: "hashtag-style" }
    ),
  }
}

function textSection(
  input: LegacyRecord,
  validation: LegacyRecord | null | undefined
): SettingsSection {
  const attrs = input?.attrs ?? {}

  return {
    id: "text",
    label: SECTION_LABEL.text,
    fields: [
      /*
       * THE AFFIX AND ITS SPACE FLAG ARE ONE ROW, as they now are on the
       * destination screens — the same two settings were a paired checkbox
       * there and two loose toggle rows here, so the same decision looked like
       * two different features depending which screen you opened.
       *
       * THE `#` RULE IS AN ERROR ON THE VALUE, NOT A STANDING NOTE. It used to
       * read "Prefixes may not contain '#'. Need hashtags?" under every prefix
       * box forever, including the overwhelming majority that have no `#` in
       * them. Now it appears when there is actually a hashtag in the field and
       * clears when there is not. The link survives, because "put it in
       * Hashtags instead" is the fix.
       */
      {
        id: "prefix-text",
        name: "input.attrs.prefixText",
        label: "Begin posts with",
        kind: "input",
        value: text(attrs.prefixText),
        /* An EXAMPLE, not the label again. "Start items with" was a restatement
           of "Begin posts with", which tells the reader nothing. */
        placeholder: "e.g. Sponsored:",
        help: "This goes after anything the destination adds, and before the post text.",
        ...affixHashtagFault(
          text(attrs.prefixText),
          "You can add hashtags on the Hashtags tab."
        ),
        pairedCheck: {
          id: "prefix-space",
          name: "input.attrs.prefixSpace",
          label: "Disable space after prefix",
          checked: flag(attrs.prefixSpace),
        },
      },
      {
        id: "postfix-text",
        name: "input.attrs.postfixText",
        label: "End posts with",
        kind: "input",
        value: text(attrs.postfixText),
        placeholder: "e.g. via @yourbrand",
        help: "This goes right after the post text, before anything the destination adds.",
        ...affixHashtagFault(
          text(attrs.postfixText),
          "You can add hashtags on the Hashtags tab."
        ),
        pairedCheck: {
          id: "postfix-space",
          name: "input.attrs.postfixSpace",
          label: "Disable space before suffix",
          checked: flag(attrs.postfixSpace),
        },
      },
      {
        id: "rules",
        hint: "Rules run top to bottom on the raw item text. Prefixes, suffixes and hashtags are added afterwards, so rules never touch them.",
        name: "input.rules",
        label: "Find and replace",
        kind: "rules",
        rules: textRules(input, validation),
        span: "full",
      },
    ],
  }
}

function filtersSection(input: LegacyRecord): SettingsSection {
  /* The route's Filters tab is the same one; see `filterFields`. */
  return {
    id: "filters",
    label: SECTION_LABEL.filters,
    fields: filterFields(input, "input"),
  }
}

function locationSection(input: LegacyRecord): SettingsSection {
  const useGeo = flag(input.useGeo)
  const fields: SettingsField[] = [
    {
      id: "use-geo",
      hint: "We pass your item's location to X when we post. You also need location turned on in your X account settings.",
      name: "input.useGeo",
      label: "Use location",
      kind: "toggle",
      checked: useGeo,
    },
  ]

  /* Legacy withholds the map and the override until location is on, and there
     is nothing for either to say while it is off. */
  if (useGeo) {
    const stored = Array.isArray(input.defaultGeo) ? input.defaultGeo : []

    fields.push(
      {
        id: "default-geo",
        hint: "We send these coordinates even when an item carries no location of its own.",
        name: "input.defaultGeo",
        label: "Default location",
        kind: "map",
        /*
         * `[lat, lng]` as strings off the record — it is stored serialized and
         * comes back as an array — and null rather than a fallback point when
         * it is unset, because the map draws no pin for null. A feed with no
         * default location must not look like it has one.
         */
        coords:
          stored.length === 2
            ? { lat: parseFloat(stored[0]), lng: parseFloat(stored[1]) }
            : null,
        /* `mapCenter` is left to the control's own default: it already falls
           back to the stored point, then to legacy's neutral starting view. */
        span: "full",
        help: "Click the map to drop a pin. Click the pin, or Clear location, to remove it.",
      },
      {
        id: "override-item-geo",
        hint: "Your default location wins over any location the item already carries.",
        name: "input.overrideItemGeo",
        label: "Default location overrides item location",
        kind: "toggle",
        checked: flag(input.overrideItemGeo),
      }
    )
  }

  return {
    id: "location",
    label: SECTION_LABEL.location,
    /* Both revealed fields answer to the switch above them. Still gated in the
       data, as the comment above says — this only changes how they draw. */
    fields: fields.map((field, i) =>
      i === 0 ? field : { ...field, dependsOn: "use-geo" }
    ),
  }
}

function advancedSection(
  input: LegacyRecord,
  clean: LegacyRecord | null | undefined,
  source: SourceType,
  ctx: FeedSectionsContext
): SettingsSection {
  const { constants } = ctx
  const attrs = input?.attrs ?? {}
  const fields: SettingsField[] = []

  /* The six that only mean anything for a parsed feed. */
  if (source === "rss") {
    fields.push(
      {
        id: "prefer-summary",
        hint: "We fall back to the other one when your first choice is missing.",
        name: "input.preferSummary",
        label: "Body posting options",
        kind: "segmented",
        options: options([
          { value: 1, label: "Prefer summary content" },
          { value: 0, label: "Prefer full content" },
        ]),
        value: choice(input.preferSummary) ?? "0",
      },
      {
        id: "media-order",
        name: "input.mediaOrder",
        label: "Image selection order",
        kind: "segmented",
        options: options([
          { value: 0, label: "Open Graph tags first" },
          { value: 1, label: "Feed content first" },
        ]),
        value: choice(input.mediaOrder) ?? String(constants.MEDIA_OG_FIRST),
      },
      {
        id: "use-date-flag",
        hint: "An item whose timestamp changes counts as new, and can post again within your other limits.",
        name: "input.useDateFlag",
        label: "Republish items with updated timestamps",
        kind: "toggle",
        checked: flag(input.useDateFlag),
      },
      {
        id: "ignore-orig-link",
        hint: "FeedBurner sources only. By default we shorten the original link rather than FeedBurner's tracking link.",
        name: "input.ignoreOrigLink",
        label: "Pass FeedBurner links to your link shortener",
        kind: "toggle",
        checked: flag(input.ignoreOrigLink),
      },
      {
        id: "skip-no-title",
        name: "input.skipNoTitleFlag",
        label: "Ignore items with no title",
        kind: "toggle",
        checked: flag(input.skipNoTitleFlag),
      },
      {
        id: "ignore-sort-date",
        name: "input.ignoreSortDate",
        label: "Do not sort items by timestamp",
        kind: "toggle",
        checked: flag(input.ignoreSortDate),
      }
    )
  }

  /*
   * PHOTO POSTING IS ONE-WAY ON A BASIC ACCOUNT, and deliberately so (I6a).
   * The control is withheld once it is on, so it can be switched on and never
   * off. The comparison is against the SAVED record rather than the working
   * one, so the toggle does not vanish under the user the instant they flip it.
   */
  const photoPostingHidden =
    ctx.routeViewType !== constants.ROUTE_VIEW_ADVANCED && flag(clean?.postImages)

  /* Pinterest is a FEED SUBTYPE now, so it reads as "rss" above — but a record
     still carrying the retired `INPUT_TYPE_PINTEREST` has no photo posting,
     and `InputAdvancedPanel` excludes it by type rather than by source. */
  const retiredPhotoless =
    parseInt(String(input?.type ?? ""), 10) === constants.INPUT_TYPE_PINTEREST

  if (source !== "instagram" && !retiredPhotoless && !photoPostingHidden) {
    fields.push({
      id: "post-images",
      hint: "When we find a shareable image in an item we make a photo post instead of a text one.",
      name: "input.postImages",
      label: "Enable photo posting",
      kind: "toggle",
      checked: flag(input.postImages),
    })
  }

  if (source === "twitter") {
    fields.push(
      {
        id: "exclude-replies",
        help: "Replies from this account are skipped.",
        name: "input.attrs.exclude_replies",
        label: "Exclude replies",
        kind: "toggle",
        checked: flag(attrs.exclude_replies),
      },
      {
        id: "include-rts",
        help: "Reposts from this account are treated as items.",
        name: "input.attrs.include_rts",
        label: "Include retweets",
        kind: "toggle",
        checked: flag(attrs.include_rts),
      }
    )
  }

  return { id: "advanced", label: SECTION_LABEL.advanced, fields }
}

/* ────────────────────────────────────────────────────────── the builder ── */

/**
 * The sources offered the Location tab.
 *
 * FOUR BACKENDS READ `i_defaultGeo`, not three: `InputFeed`, `InputTikTok`,
 * `InputYoutube` and `InputTwitter` all stamp it onto each item as
 * `geoLat` / `geoLong` at pull time. Facebook and Instagram have no reader at
 * all, so their tab really would save a point nothing posts.
 *
 * X IS WITHHELD BY DECISION, NOT BECAUSE NOTHING READS IT. No tile offers an X
 * source any more, so the tab would only ever reach a record created before
 * that. Putting X back is one entry in this list.
 */
const LOCATION_SOURCES: SourceType[] = ["rss", "tiktok", "youtube"]

/**
 * The seven tabs of an automation's settings, pruned and branched for this
 * record.
 *
 * `clean` is the saved input — the same record `InputEdit` diffs against for
 * its dirty flag. Only one field reads it (photo posting, above), but it has to
 * be the saved value rather than the working one or the control disappears
 * mid-edit.
 */
export function buildFeedSections(
  input: LegacyRecord | null | undefined,
  clean: LegacyRecord | null | undefined,
  ctx: FeedSectionsContext
): SettingsSection[] {
  if (!input || input.id === undefined) return []

  const source = feedSourceType(input, ctx.constants)
  const sections = [
    detailSection(input, clean, source, ctx),
    updatesSection(input, source, ctx),
    hashtagsSection(input, ctx),
    textSection(input, ctx.validation),
    filtersSection(input),
    locationSection(input),
    advancedSection(input, clean, source, ctx),
  ]

  const withErrors = attachErrors(sections, {
    tree: ctx.validation,
    prefix: "input",
    fieldErrors: ctx.fieldErrors,
  })

  /*
   * TABS WITH NOTHING BEHIND THEM.
   *
   * Legacy prunes both of these, but only for Instagram Professional and only
   * on the edit path — `InputEditorButtonsMixin` returns all seven tabs when
   * the link prefix is the create flow's, before it ever tests the type, and
   * it tests `INPUT_TYPE_INSTAGRAM_BUSINESS` alone. Pruning the same way on
   * both paths, and for the retired Instagram type too, is deliberate.
   */
  return withErrors.filter((section) => {
    if (section.id === "location") return LOCATION_SOURCES.includes(source)

    /* Instagram's Advanced tab would be empty rather than merely sparse: the
       six feed-parsing rows are RSS-only, photo posting is withheld for
       Instagram, and the two that are left are X's. */
    if (section.id === "advanced") return source !== "instagram"

    return true
  })
}
